// Password rule DSL — the AI's tool surface for this demo.
//
// Each rule is a named predicate the deterministic password generator
// must satisfy. The AI proposes new rules from this same library when
// the andon cord pulls; the proposals never invent new predicate kinds,
// only new instances of existing kinds. That's the "closed action space"
// half of the architecture made literal — the AI can't escape this
// list, so the runtime can validate any proposal mechanically.
//
// Eight predicate kinds chosen to give the AI compositional headroom
// without exploding the surface. Each kind has a runner (boolean
// "does this candidate pass?"), a pretty-printer (the .agi-ish form
// rendered in UI later), and a difficulty estimate (used by the stub
// responder to pick "the next sensible challenge").

// ─── Predicate definitions ──────────────────────────────────────────────────

/** Comparison ops for numeric predicates. */
export type Op = "=" | "!=" | ">" | ">=" | "<" | "<=";

/** Character classes the DSL recognizes. The generator uses these to
 *  pick from when sampling; predicates use them to check membership. */
export type CharClass =
  | "digit"   // 0-9
  | "lower"   // a-z
  | "upper"   // A-Z
  | "letter"  // a-zA-Z
  | "symbol"; // a small punctuation set

export type Predicate =
  /** Total length compared against n. */
  | { kind: "LENGTH"; op: Op; n: number }
  /** Password must include at least one char from the named class. */
  | { kind: "CONTAINS"; class: CharClass }
  /** Password must NOT include the literal substring `pattern`. */
  | { kind: "NOT_CONTAINS"; pattern: string }
  /** The char at index i (0-based) must be in the named class. */
  | { kind: "POSITION"; i: number; class: CharClass }
  /** Sum of digit chars (treated as integers) compared against n. */
  | { kind: "SUM_OF_DIGITS"; op: Op; n: number }
  /** No two consecutive characters are equal. */
  | { kind: "NO_REPEAT" }
  /** The first char must be in the named class. */
  | { kind: "STARTS_WITH"; class: CharClass }
  /** No run of identical characters of length >= n (n >= 2). */
  | { kind: "NO_RUN_OF_LENGTH"; n: number };

export interface Rule {
  /** Stable id — used by the ledger to reference this rule across
   *  mutations even if its predicate changes shape. */
  id: string;
  /** Short human-readable handle (also rendered in the .agi-ish form). */
  name: string;
  predicate: Predicate;
}

// ─── Character class helpers ────────────────────────────────────────────────

const SYMBOLS = "!@#$%^&*()-_=+[]{};:,.<>?/";

export function charsOfClass(cls: CharClass): string {
  switch (cls) {
    case "digit":  return "0123456789";
    case "lower":  return "abcdefghijklmnopqrstuvwxyz";
    case "upper":  return "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    case "letter": return charsOfClass("lower") + charsOfClass("upper");
    case "symbol": return SYMBOLS;
  }
}

function isInClass(ch: string, cls: CharClass): boolean {
  return charsOfClass(cls).includes(ch);
}

function cmp(a: number, op: Op, b: number): boolean {
  switch (op) {
    case "=":  return a === b;
    case "!=": return a !== b;
    case ">":  return a > b;
    case ">=": return a >= b;
    case "<":  return a < b;
    case "<=": return a <= b;
  }
}

// ─── Predicate evaluation ───────────────────────────────────────────────────

/** Does `candidate` satisfy `predicate`? Pure function. */
export function evaluate(predicate: Predicate, candidate: string): boolean {
  switch (predicate.kind) {
    case "LENGTH":
      return cmp(candidate.length, predicate.op, predicate.n);

    case "CONTAINS":
      for (const ch of candidate) if (isInClass(ch, predicate.class)) return true;
      return false;

    case "NOT_CONTAINS":
      return !candidate.includes(predicate.pattern);

    case "POSITION":
      if (predicate.i < 0 || predicate.i >= candidate.length) return false;
      return isInClass(candidate[predicate.i], predicate.class);

    case "SUM_OF_DIGITS": {
      let sum = 0;
      for (const ch of candidate) {
        if (ch >= "0" && ch <= "9") sum += Number(ch);
      }
      return cmp(sum, predicate.op, predicate.n);
    }

    case "NO_REPEAT":
      for (let i = 1; i < candidate.length; i++) {
        if (candidate[i] === candidate[i - 1]) return false;
      }
      return true;

    case "STARTS_WITH":
      return candidate.length > 0 && isInClass(candidate[0], predicate.class);

    case "NO_RUN_OF_LENGTH": {
      let run = 1;
      for (let i = 1; i < candidate.length; i++) {
        if (candidate[i] === candidate[i - 1]) {
          run++;
          if (run >= predicate.n) return false;
        } else {
          run = 1;
        }
      }
      return true;
    }
  }
}

/** Pure check for a whole rule set. */
export function passesAll(rules: readonly Rule[], candidate: string): boolean {
  for (const r of rules) if (!evaluate(r.predicate, candidate)) return false;
  return true;
}

// ─── Length as a ratcheting target ──────────────────────────────────────────
//
// There is always exactly one LENGTH rule in play (the starter ships it,
// the lifecycle only ever REPLACES it in place — never adds a second).
// That makes length a single monotonic dial the AI can crank upward via
// the "rule_ratchet" tier. These helpers are what the lifecycle and the
// responders read to know "where is length now, and how far can it go."

/** Smallest length the puzzle ever starts at. */
export const MIN_LENGTH = 3;
/** Hard ceiling on the ratchet — keeps the generator's search space sane
 *  and gives the andon loop a real "tier rejected: cap reached" event to
 *  show once the AI tries to push past it. */
export const MAX_LENGTH = 24;

/** The single LENGTH rule in a ruleset, if present. */
export function findLengthRule(rules: readonly Rule[]): Rule | undefined {
  return rules.find((r) => r.predicate.kind === "LENGTH");
}

/** The effective target length the generator will build to. Equality and
 *  ">=" treat n as the target/floor; ">" means n+1. Returns 0 when there
 *  is no LENGTH rule at all. */
export function currentLengthTarget(rules: readonly Rule[]): number {
  const lr = findLengthRule(rules);
  if (!lr || lr.predicate.kind !== "LENGTH") return 0;
  const p = lr.predicate;
  return p.op === ">" ? p.n + 1 : p.n;
}

// ─── Complexity curriculum ──────────────────────────────────────────────────
//
// Escalating a password well is not "make it longer." It's moving along
// several independent dimensions of difficulty — character variety, then
// structure, then arithmetic, then exclusions — with length as just ONE
// lever, best used sparingly. Left to itself a small model takes the safe
// path (raise length forever), so the harness analyzes what's already
// covered and hands the AI a concrete "next sensible move." The AI is still
// free to deviate; this just scaffolds it toward the curriculum.

export interface ComplexityCoverage {
  length: number;
  /** Lowercase letters are allowed/required somewhere. */
  hasLower: boolean;
  /** Uppercase letters are allowed/required somewhere. */
  hasUpper: boolean;
  /** Symbols are allowed/required somewhere. */
  hasSymbol: boolean;
  /** A no-repeat / no-run structural rule is present. */
  hasStructure: boolean;
  /** A SUM_OF_DIGITS arithmetic constraint is present. */
  hasArithmetic: boolean;
  /** A NOT_CONTAINS exclusion is present. */
  hasExclusion: boolean;
  /** A POSITION/STARTS_WITH constraint beyond the opening one is present. */
  hasPositional: boolean;
}

export function analyzeComplexity(rules: readonly Rule[]): ComplexityCoverage {
  const cov: ComplexityCoverage = {
    length: currentLengthTarget(rules),
    hasLower: false, hasUpper: false, hasSymbol: false,
    hasStructure: false, hasArithmetic: false, hasExclusion: false,
    hasPositional: false,
  };
  let positionalCount = 0;
  for (const r of rules) {
    const p = r.predicate;
    switch (p.kind) {
      case "CONTAINS":
      case "STARTS_WITH":
      case "POSITION": {
        const cls = p.kind === "CONTAINS" || p.kind === "STARTS_WITH" || p.kind === "POSITION" ? p.class : null;
        if (cls === "lower" || cls === "letter") cov.hasLower = true;
        if (cls === "upper" || cls === "letter") cov.hasUpper = true;
        if (cls === "symbol") cov.hasSymbol = true;
        if (p.kind === "POSITION" || p.kind === "STARTS_WITH") positionalCount++;
        break;
      }
      case "NO_REPEAT":
      case "NO_RUN_OF_LENGTH":
        cov.hasStructure = true;
        break;
      case "SUM_OF_DIGITS":
        cov.hasArithmetic = true;
        break;
      case "NOT_CONTAINS":
        cov.hasExclusion = true;
        break;
    }
  }
  // The starter pins POSITION i=0 to digit; only count it as "positional
  // complexity" once there's more than that opening constraint.
  cov.hasPositional = positionalCount > 1;
  return cov;
}

/** A human-readable list of the complexity dimensions already in play. */
export function complexitySummary(rules: readonly Rule[]): string {
  const c = analyzeComplexity(rules);
  const have: string[] = [`length ${c.length}`];
  if (c.hasLower) have.push("lowercase");
  if (c.hasUpper) have.push("uppercase");
  if (c.hasSymbol) have.push("symbols");
  if (c.hasStructure) have.push("no-repeat/no-run");
  if (c.hasArithmetic) have.push("digit-sum");
  if (c.hasExclusion) have.push("banned-substrings");
  if (c.hasPositional) have.push("positional");
  return have.join(", ");
}

/** The next sensible escalation given current coverage — a concrete,
 *  copy-pasteable predicate suggestion. Walks the curriculum in order,
 *  returning the first unmet dimension; only reaches for length once the
 *  password already has some character + structural variety. */
export function nextComplexityHint(rules: readonly Rule[]): string {
  const c = analyzeComplexity(rules);

  // How many distinct character classes the password is already REQUIRED to
  // contain (digit is forced by the starter). Each required class needs its
  // own slot, and position 0 is pinned to a digit — so a ruleset that
  // demands N classes needs roughly N characters to be satisfiable. Before
  // suggesting another required class, make sure there's room; otherwise the
  // honest move is to stretch the length first.
  const requiredClasses = 1 + (c.hasLower ? 1 : 0) + (c.hasUpper ? 1 : 0) + (c.hasSymbol ? 1 : 0);
  const makeRoom = (classesAfter: number) =>
    `First make room — ratchet length from ${c.length} to ${classesAfter}, so the new requirement is satisfiable.`;

  if (!c.hasLower) {
    if (c.length < 2) return makeRoom(2);
    return `Go alphanumeric — let letters in: { "kind": "CONTAINS", "class": "lower" }.`;
  }
  if (!c.hasStructure)
    return `Forbid lazy patterns — { "kind": "NO_REPEAT" } (no two identical characters in a row).`;
  if (!c.hasUpper) {
    if (c.length < requiredClasses + 1) return makeRoom(requiredClasses + 1);
    return `Demand mixed case — { "kind": "CONTAINS", "class": "upper" }.`;
  }
  if (!c.hasSymbol) {
    if (c.length < requiredClasses + 1) return makeRoom(requiredClasses + 1);
    return `Require a symbol — { "kind": "CONTAINS", "class": "symbol" }.`;
  }
  // Arithmetic needs enough digit slots to be satisfiable: with the forced
  // non-digit classes eating positions, a high digit-sum is impossible
  // unless the password is long enough to carry several digits.
  const enoughDigitsForSum = c.length >= requiredClasses + 2;
  if (!c.hasArithmetic && enoughDigitsForSum)
    return `Constrain the digits — { "kind": "SUM_OF_DIGITS", "op": ">=", "n": 10 }.`;
  if (!c.hasExclusion)
    return `Ban an obvious pattern — { "kind": "NOT_CONTAINS", "pattern": "123" }.`;
  if (!c.hasPositional)
    return `Pin a position — { "kind": "POSITION", "i": 1, "class": "upper" } (uppercase is already allowed).`;
  if (!c.hasArithmetic && !enoughDigitsForSum)
    return `Make room for digit math — ratchet length from ${c.length} to ${requiredClasses + 2}, then a SUM_OF_DIGITS rule becomes satisfiable.`;
  if (c.length < MAX_LENGTH)
    return `Every dimension is covered — now stretch the length from ${c.length} to ${c.length + 2}, or add another exclusion for variety.`;
  return `Length is maxed and every dimension is covered — add another NOT_CONTAINS exclusion or a tighter positional rule. Avoid repeating your last move.`;
}

// ─── Pretty-printing (the .agi-ish surface form) ────────────────────────────

export function formatPredicate(p: Predicate): string {
  switch (p.kind) {
    case "LENGTH":          return `LENGTH ${p.op} ${p.n}`;
    case "CONTAINS":        return `CONTAINS class=${p.class}`;
    case "NOT_CONTAINS":    return `NOT_CONTAINS pattern=${JSON.stringify(p.pattern)}`;
    case "POSITION":        return `POSITION i=${p.i} class=${p.class}`;
    case "SUM_OF_DIGITS":   return `SUM_OF_DIGITS ${p.op} ${p.n}`;
    case "NO_REPEAT":       return `NO_REPEAT`;
    case "STARTS_WITH":     return `STARTS_WITH class=${p.class}`;
    case "NO_RUN_OF_LENGTH":return `NO_RUN_OF_LENGTH n=${p.n}`;
  }
}

export function formatRule(r: Rule): string {
  return `RULE ${r.name} { ${formatPredicate(r.predicate)} }`;
}

export function formatRuleset(rules: readonly Rule[]): string {
  return rules.map(formatRule).join("\n");
}

// ─── Difficulty estimate (for the stub responder) ───────────────────────────
//
// A rough "how much does this predicate narrow the candidate space"
// score. Not statistically rigorous — good enough for the stub
// responder to pick "the next sensible challenge" without flailing.
// A real AI responder ignores these and proposes via natural reasoning.

export function difficultyEstimate(p: Predicate): number {
  switch (p.kind) {
    case "LENGTH":          return p.n * 1.5;
    case "CONTAINS":        return p.class === "symbol" ? 3 : 2;
    case "NOT_CONTAINS":    return p.pattern.length * 1.2;
    case "POSITION":        return 4;
    case "SUM_OF_DIGITS":   return 5;
    case "NO_REPEAT":       return 4;
    case "STARTS_WITH":     return 2;
    case "NO_RUN_OF_LENGTH":return 3;
  }
}

export function rulesetDifficulty(rules: readonly Rule[]): number {
  return rules.reduce((sum, r) => sum + difficultyEstimate(r.predicate), 0);
}

// ─── Starter ruleset ────────────────────────────────────────────────────────

/** The opening puzzle — gentle on purpose. Three digits, period. */
export function starterRules(): Rule[] {
  return [
    { id: "r_length_3",   name: "length_3",   predicate: { kind: "LENGTH", op: "=", n: 3 } },
    { id: "r_only_digits",name: "only_digits",predicate: { kind: "POSITION", i: 0, class: "digit" } },
    // The third rule extends "only_digits" to all positions by composition
    // with the LENGTH=3 rule. The generator picks per-position from the
    // alphabet — when POSITION rules don't restrict an index, it uses
    // the union of CONTAINS classes, which we widen via the third rule:
    { id: "r_all_digits", name: "all_digits", predicate: { kind: "CONTAINS", class: "digit" } },
  ];
}
